import React, { useState, useEffect } from 'react';
import { locations } from '../data/mockData';
import { getSettings, createRegistration } from '../services/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { ChevronRight, Trophy, UserPlus } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import PrivacyCheckbox from '../components/PrivacyCheckbox';
import EditionsHistoryModal from '../components/EditionsHistoryModal';
import MediaPlaylistPlayer from '../components/MediaPlaylistPlayer';
import FeaturedVideo from '../components/FeaturedVideo';
import FinalistsBoard from '../components/FinalistsBoard';
import FinalCountdown from '../components/FinalCountdown';
import { logError } from '../lib/logError';

// Immagini da Altri Format per la galleria (allineate ai logo/preview aggiornati)
const formatImages = [
  "https://static.wixstatic.com/media/e93d0f_7127cfcadfd044c0a6290f277635dd2a~mv2.jpg/v1/fill/w_4270,h_1098,al_c,q_90,usm_0.66_1.00_0.01,enc_avif,quality_auto/e93d0f_7127cfcadfd044c0a6290f277635dd2a~mv2.jpg",
  "https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/u3vrmvho_CypherKilla_Negative.pdf.png",
  "https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/xkjh9ub0_freestylemania.webp",
  "https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/omwekie4_YOCLAS-MTV-SPIT-rap-show-brand-logo.jpg",
  "https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/ubuk1l1b_fuori%20per%20il%20cash%20logo.png",
  "https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/y3gkpnb0_Progetto%20senza%20titolo%20%287%29.jpg",
  "https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/j68d7pe4_3mc%261dj-logo.avif",
  "https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/18wni9yu_2real.gif",
  "https://customer-assets.emergentagent.com/job_site-mirror-165/artifacts/ot2iczsz_logo%20microphone%20masta.avif"
];

const DEFAULT_SHORTS = [
  { type: 'youtube', id: 'P3MLM3vLca0' },
  { type: 'youtube', id: 'oDqPRpclEhE' },
  { type: 'youtube', id: 'a5-nPE9XqIg' },
  { type: 'tiktok', id: '7160287447480421638' },
  { type: 'youtube', id: 'pRrqqnBfv7A' },
  { type: 'youtube', id: '5keISxq8Ik8' },
  { type: 'youtube', id: 'tWbJls6hDMo' },
  { type: 'youtube', id: 'rA_RvjKvFLs' },
  { type: 'tiktok', id: '7632296643584429334' },
  { type: 'tiktok', id: '7502551156309232918' },
];

const HomePage = () => {
  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({
    nome: '', cognome: '', email: '', prefisso: '+39', telefono: '', nickname: '', location: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [prizesOpen, setPrizesOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [shorts, setShorts] = useState(DEFAULT_SHORTS);
  const [homeSettings, setHomeSettings] = useState(null);
  const [finalists, setFinalists] = useState([]);
  const location = useLocation();

  useEffect(() => {
    if (new URLSearchParams(location.search).get('iscrizione') === '1') {
      setDialogOpen(true);
      window.history.replaceState(null, '', '/');
    }
  }, [location.search]);

  useEffect(() => {
    getSettings().then(setSettings).catch((e) => logError('[home] settings non caricate', e));
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/site-content/home-settings`)
      .then(r => r.json())
      .then(setHomeSettings)
      .catch((e) => logError('[home] home-settings non caricate', e));
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/site-content/finalists`)
      .then(r => r.json())
      .then(d => { if (Array.isArray(d.items)) setFinalists(d.items); })
      .catch(() => {});
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/site-content/shorts`)
      .then(r => r.json())
      .then(d => { if (Array.isArray(d.items) && d.items.length) setShorts(d.items); })
      .catch(() => {});
  }, []);

  // Galleria animata - cambia immagine ogni 2 secondi
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % formatImages.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const s = settings || {};

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome || !formData.cognome || !formData.email || !formData.nickname) {
      toast({ title: "Errore", description: "Compila tutti i campi obbligatori", variant: "destructive" });
      return;
    }
    if (!privacyAccepted) {
      toast({ title: "Errore", description: "Devi accettare l'informativa sulla privacy per procedere", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      await createRegistration(formData);
      setSubmitted(true);
      toast({ title: "Registrazione inviata!", description: "Ti ringraziamo per la registrazione al nostro evento." });
      setTimeout(() => setDialogOpen(false), 2000);
    } catch (err) {
      toast({ title: "Errore", description: "Si è verificato un errore. Riprova.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setFormData({ nome: '', cognome: '', email: '', prefisso: '+39', telefono: '', nickname: '', location: '' });
    setPrivacyAccepted(false);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div style={{ backgroundColor: 'var(--tp-black)' }}>
      {/* Description Banner */}
      <section className="py-4 sm:py-6 border-b border-gray-800" style={{ backgroundColor: 'var(--tp-dark)' }}>
        <div className="max-w-5xl mx-auto px-4 text-center">
          <p className="text-white font-bold text-sm sm:text-base tracking-widest" style={{ fontFamily: 'Oswald, sans-serif' }}>
            SITO DEL PRIMO TORNEO NAZIONALE DI RAP E FREESTYLE ITALIANO
          </p>
        </div>
      </section>

      {/* Countdown finale nazionale (gestibile da admin) */}
      {homeSettings?.countdown_enabled && homeSettings.countdown_date && (
        <FinalCountdown date={homeSettings.countdown_date} label={homeSettings.countdown_label} />
      )}

      {/* Video in evidenza (gestibile da admin) */}
      {homeSettings && <FeaturedVideo url={homeSettings.featured_video_url} title={homeSettings.featured_video_title} />}

      {/* Hero Videos: SPOT (sinistra 16:9) + PROSSIME DATE (destra 9:16 verticale) */}
      <section className="px-4 py-6 sm:py-8" style={{ backgroundColor: 'var(--tp-black)' }}>
        <div className="max-w-6xl mx-auto flex flex-row items-end justify-center gap-3 sm:gap-6">
          {/* SPOT — 16:9 */}
          <div className="flex flex-col items-center">
            <p
              className="text-center text-white font-bold text-xs sm:text-sm md:text-base tracking-widest mb-2 sm:mb-3"
              style={{ fontFamily: 'Oswald, sans-serif', letterSpacing: '2px' }}
            >
              SPOT
            </p>
            <div
              className="relative bg-black rounded-xl overflow-hidden border border-gray-800 shadow-2xl"
              style={{
                aspectRatio: '16 / 9',
                height: 'clamp(120px, 35vw, 450px)',
                width: 'auto',
              }}
            >
              <iframe
                src="https://www.youtube.com/embed/ukdv48Ud3Bg?autoplay=1&mute=1&loop=1&playlist=ukdv48Ud3Bg,td2x5nRZM_w,l9Jb4bFHCMs,7cEM61yL_8Y,OCC7zKqrpDI,5XcTdMOU7Cg,dsfC1b96gzs&controls=1&modestbranding=1&rel=0&playsinline=1"
                title="Tecniche Perfette — SPOT"
                data-testid="home-hero-youtube"
                className="absolute inset-0 w-full h-full"
                style={{ border: 0 }}
                allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                loading="eager"
              />
            </div>
          </div>

          {/* PROSSIME DATE — 9:16 verticale, stessa altezza dello SPOT */}
          <div className="flex flex-col items-center">
            <p
              className="text-center font-bold text-xs sm:text-sm md:text-base tracking-widest mb-2 sm:mb-3"
              style={{ fontFamily: 'Oswald, sans-serif', letterSpacing: '2px', color: 'var(--tp-red)' }}
            >
              SHORTS
            </p>
            <div
              className="relative bg-black rounded-xl overflow-hidden border border-red-600/40 shadow-2xl"
              style={{
                aspectRatio: '9 / 16',
                height: 'clamp(120px, 35vw, 450px)',
                width: 'auto',
              }}
            >
              <MediaPlaylistPlayer
                key={shorts.map(s => s.id).join(',')}
                aspect="9/16"
                testId="home-hero-prossime-date"
                items={shorts}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Tabellone finalisti (interruttore + titolo da admin) */}
      {homeSettings?.finalists_enabled && <FinalistsBoard title={homeSettings.finalists_title} finalists={finalists} />}

      {/* CTA Torneo: Tabelloni Scorse Edizioni (+ Iscrizioni / Premi nascosti) */}
      <section className="py-10 sm:py-16 px-4" style={{ backgroundColor: 'var(--tp-black)' }}>
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-center flex-wrap gap-4 sm:gap-6">
            <EditionsHistoryModal />
            {homeSettings?.show_registration_btn && (
            <button
              type="button"
              onClick={() => setDialogOpen(true)}
              data-testid="open-registration-btn"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-red-600 to-red-800 hover:from-red-500 hover:to-red-700 text-white font-bold text-sm sm:text-base shadow-xl transition-all hover:scale-105 border border-red-400/40"
              style={{ fontFamily: 'Oswald, sans-serif', letterSpacing: '0.5px' }}
            >
              <UserPlus size={18} />
              ISCRIZIONI 2026
            </button>
            )}
            {homeSettings?.show_prizes_btn && (
            <button
              type="button"
              onClick={() => setPrizesOpen(true)}
              data-testid="open-prizes-btn"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-yellow-500 to-yellow-700 hover:from-yellow-400 hover:to-yellow-600 text-black font-bold text-sm sm:text-base shadow-xl transition-all hover:scale-105 border border-yellow-300/40"
              style={{ fontFamily: 'Oswald, sans-serif', letterSpacing: '0.5px' }}
            >
              <Trophy size={18} />
              I PREMI DEL VINCITORE NAZIONALE
            </button>
            )}
          </div>
          {homeSettings?.show_registration_btn && (
          <p className="text-center text-gray-400 text-base sm:text-lg mt-6 max-w-2xl mx-auto">
            Partecipa al più grande torneo nazionale di improvvisazione in rima. Registrati ora per le selezioni 2026!
          </p>
          )}
        </div>
      </section>

      {/* Dialog Form Iscrizioni (controllato via bottone ISCRIZIONI 2026) */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent aria-describedby={undefined} className="bg-gray-900 border-gray-700 text-white max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '2px' }}>
              ISCRIZIONI 2026
            </DialogTitle>
          </DialogHeader>

          <div className="mt-4">
            {submitted ? (
              <div className="text-center py-8 sm:py-10">
                <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: 'var(--tp-red)' }}>
                  <Trophy size={32} className="text-white" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  Registrazione completata!
                </h3>
                <p className="text-gray-400 text-sm sm:text-base">Ti ringraziamo per la registrazione al nostro evento. Ci vediamo presto!</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 text-xs mb-1 tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>NOME *</label>
                    <Input value={formData.nome} onChange={e => handleChange('nome', e.target.value)}
                      className="bg-gray-800 border-gray-600 text-white placeholder:text-gray-500 focus:border-red-500" placeholder="Il tuo nome" />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-xs mb-1 tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>COGNOME *</label>
                    <Input value={formData.cognome} onChange={e => handleChange('cognome', e.target.value)}
                      className="bg-gray-800 border-gray-600 text-white placeholder:text-gray-500 focus:border-red-500" placeholder="Il tuo cognome" />
                  </div>
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1 tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>EMAIL *</label>
                  <Input type="email" value={formData.email} onChange={e => handleChange('email', e.target.value)}
                    className="bg-gray-800 border-gray-600 text-white placeholder:text-gray-500 focus:border-red-500" placeholder="La tua email" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-gray-400 text-xs mb-1 tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>PREFISSO</label>
                    <Input value={formData.prefisso} onChange={e => handleChange('prefisso', e.target.value)}
                      className="bg-gray-800 border-gray-600 text-white focus:border-red-500" />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-gray-400 text-xs mb-1 tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>TELEFONO</label>
                    <Input value={formData.telefono} onChange={e => handleChange('telefono', e.target.value)}
                      className="bg-gray-800 border-gray-600 text-white placeholder:text-gray-500 focus:border-red-500" placeholder="Il tuo numero" />
                  </div>
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1 tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>NICKNAME / SOPRANNOME *</label>
                  <Input value={formData.nickname} onChange={e => handleChange('nickname', e.target.value)}
                    className="bg-gray-800 border-gray-600 text-white placeholder:text-gray-500 focus:border-red-500" placeholder="Il tuo nome d'arte" />
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1 tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>DOVE PARTECIPERAI?</label>
                  <Select onValueChange={(val) => handleChange('location', val)}>
                    <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                      <SelectValue placeholder="Seleziona città" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map(loc => (
                        <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <PrivacyCheckbox
                  checked={privacyAccepted}
                  onCheckedChange={setPrivacyAccepted}
                  formType="iscrizione"
                />
                <Button type="submit" disabled={loading}
                  className="w-full py-5 text-base font-bold tracking-wider text-white"
                  style={{ backgroundColor: 'var(--tp-red)', fontFamily: 'Oswald, sans-serif' }}>
                  {loading ? 'INVIO IN CORSO...' : 'REGISTRATI'}
                </Button>
              </form>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Premi Vincitore Nazionale */}
      <Dialog open={prizesOpen} onOpenChange={setPrizesOpen}>
        <DialogContent aria-describedby={undefined} className="bg-gray-900 border-yellow-600/40 text-white max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl sm:text-3xl font-bold flex items-center gap-3" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '2px' }}>
              <Trophy size={28} className="text-yellow-400" />
              I PREMI DEL VINCITORE NAZIONALE
            </DialogTitle>
          </DialogHeader>

          <div className="mt-4 space-y-5">
            <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
              Il vincitore del torneo nazionale Tecniche Perfette si aggiudica un montepremi composto da prestigiosi riconoscimenti messi in palio dai nostri partner.
            </p>

            <div className="space-y-3">
              <div className="rounded-lg border border-yellow-600/30 bg-yellow-900/10 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Trophy size={16} className="text-yellow-400" />
                  <h3 className="text-yellow-400 font-bold text-base" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    PRIMO PREMIO — 2K
                  </h3>
                </div>
                <ul className="text-gray-300 text-sm leading-relaxed list-disc list-inside space-y-1 mt-2">
                  <li>Entrata nella <strong className="text-white">WALL OF FAME</strong> di Tecniche Perfette</li>
                  <li>Cintura ufficiale del Campione Nazionale</li>
                  <li>
                    Contratto Discografico con distribuzione <strong className="text-white">BELIEVE</strong>
                    <span className="block text-xs text-gray-400 italic mt-0.5">(realizzazione, distribuzione, promozione)</span>
                  </li>
                  <li>
                    <span className="inline-flex items-center gap-2">
                      <img
                        src="https://logo.clearbit.com/thauruspublishing.it"
                        alt="Thaurus Publishing"
                        data-testid="thaurus-logo"
                        className="w-5 h-5 rounded bg-white/90 object-contain inline-block"
                        onError={(e) => {
                          if (!e.target.dataset.fallback) {
                            e.target.dataset.fallback = '1';
                            e.target.src = 'https://www.google.com/s2/favicons?domain=thaurus.it&sz=64';
                          } else {
                            e.target.style.display = 'none';
                          }
                        }}
                      />
                      <span>Contratto con <strong className="text-white">THAURUS PUBLISHING</strong> per la realizzazione di un EP</span>
                    </span>
                    <span className="block text-xs text-gray-400 italic mt-0.5">(negli studi e con i produttori di Thaurus Publishing)</span>
                  </li>
                  <li>Un anno di partecipazione alle date del circuito <strong className="text-white">MAS NETWORK</strong></li>
                  <li>
                    Partecipazione sul palco dell'<strong className="text-white">HIP HOP KEMP</strong>
                    <span className="block text-xs text-gray-400 italic mt-0.5">(il festival Hip Hop più grande d'Europa)</span>
                  </li>
                  <li>Partecipazione al format <strong className="text-white">"ALL BARS"</strong></li>
                  <li>Merch ufficiale e premi degli sponsor partner</li>
                </ul>
              </div>

              <div className="rounded-lg border border-gray-700 bg-gray-800/40 p-4">
                <h3 className="text-gray-200 font-bold text-sm mb-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  ALTRI PREMI
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  <strong className="text-white">Premio Giuria</strong> (Delivery e Flow) — <strong className="text-white">Premio Punchline</strong>
                </p>
              </div>
            </div>

            <p className="text-center text-gray-500 text-xs italic pt-2">
              Il dettaglio completo dei premi 2026 verrà comunicato sui canali ufficiali T.P.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Links */}
      <section className="py-8 sm:py-12 px-4" style={{ backgroundColor: 'var(--tp-dark)' }}>
        <div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8">
          <Link to="/regolamento" className="group block">
            <div className="rounded-lg overflow-hidden border border-gray-700 hover:border-red-500 transition-colors duration-300">
              <img src={s.regolamento_image || "https://static.wixstatic.com/media/e93d0f_22fab982f7d84186bc2cf8578b26d50d~mv2.png/v1/fill/w_403,h_93,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/REGOLAMENTO.png"} alt="Regolamento" className="w-full h-16 sm:h-20 object-contain bg-white p-2" />
              <div className="p-3 sm:p-4 flex items-center justify-between" style={{ backgroundColor: 'var(--tp-gray)' }}>
                <span className="text-white font-semibold text-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>REGOLAMENTO</span>
                <ChevronRight size={20} className="text-gray-400 group-hover:text-red-500 transition-colors" />
              </div>
            </div>
          </Link>
          <Link to="/altri-format" className="group block">
            <div className="rounded-lg overflow-hidden border border-gray-700 hover:border-red-500 transition-colors duration-300">
              {/* Galleria animata */}
              <div className="relative w-full h-16 sm:h-20 bg-black overflow-hidden">
                {formatImages.map((img, idx) => (
                  <img 
                    key={img}
                    src={img} 
                    alt={`Format ${idx + 1}`} 
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                      idx === currentImageIndex ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                ))}
              </div>
              <div className="p-3 sm:p-4 flex items-center justify-between" style={{ backgroundColor: 'var(--tp-gray)' }}>
                <span className="text-white font-semibold text-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>SCOPRI I NOSTRI FORMAT</span>
                <ChevronRight size={20} className="text-gray-400 group-hover:text-red-500 transition-colors" />
              </div>
            </div>
          </Link>
        </div>
      </section>

    </div>
  );
};

export default HomePage;
